/**
 * useLandscapeToast Hook
 *
 * Shows a one-time toast suggesting landscape orientation for mobile combat.
 * Uses localStorage to ensure it only shows once per device.
 *
 * @module hooks/useLandscapeToast
 */

import { useEffect, useRef } from 'react';
import { useViewport } from './useViewport';
import { useToast } from './useToast';

const STORAGE_KEY = 'dow:mobile-combat:landscape-toast-shown';

/**
 * Safe localStorage getter that handles SSR and errors.
 */
function safeGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Safe localStorage setter that handles SSR and errors.
 */
function safeSetItem(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage errors (quota exceeded, private browsing, etc.)
  }
}

/**
 * Hook that shows a one-time toast suggesting landscape orientation
 * when entering combat on mobile in portrait mode.
 *
 * @param combatActive - Whether combat is currently active
 *
 * @example
 * ```tsx
 * // In a component
 * useLandscapeToast(combatActive);
 * ```
 */
export function useLandscapeToast(combatActive: boolean): void {
  const viewport = useViewport();
  const toast = useToast();
  const hasShownRef = useRef(false);

  useEffect(() => {
    // Only run when combat becomes active
    if (!combatActive) {
      hasShownRef.current = false;
      return;
    }

    // Only show on mobile
    if (!viewport.isMobile) return;

    // Only show if in portrait
    if (!viewport.isPortrait) return;

    // Only show once per session (ref prevents re-showing on re-renders)
    if (hasShownRef.current) return;

    // Check if already shown in a previous session
    const alreadyShown = safeGetItem(STORAGE_KEY);
    if (alreadyShown) return;

    // Show the toast
    toast.info('Rotate device for best battle view');

    // Mark as shown for this session and persist
    hasShownRef.current = true;
    safeSetItem(STORAGE_KEY, 'true');
  }, [combatActive, viewport.isMobile, viewport.isPortrait, toast]);
}

export default useLandscapeToast;
